import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { createContext, forwardRef, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { DragDropItem } from '@/components/ui/custom/dataInput/DragDrop';
import { Button, Loading } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import { Breakpoints } from '@/utils/Breakpoint';

import { MoreActions } from './MoreActions';
import {
  CourseAdminCardActionsProps,
  CourseAdminCardBadgesProps,
  CourseAdminCardBannerProps,
  CourseAdminCardBodyProps,
  CourseAdminCardContextValue,
  CourseAdminCardDescriptionProps,
  CourseAdminCardDraggableInlineFormProps,
  CourseAdminCardEditButtonProps,
  CourseAdminCardHeaderProps,
  CourseAdminCardLeadingIconProps,
  CourseAdminCardLeadingProps,
  CourseAdminCardNestedListProps,
  CourseAdminCardOrderProps,
  CourseAdminCardProps,
  CourseAdminCardTitleBlockProps,
  CourseAdminCardTitleProps,
} from './types';

const CourseAdminCardContext = createContext<CourseAdminCardContextValue>({
  isDragging: false,
  isDraggable: false,
});

const useCourseAdminCardContext = () => useContext(CourseAdminCardContext);

const shellBaseClasses =
  'bg-base-200 border border-neutral-content rounded-2xl min-w-0 p-4';

const CourseAdminCardShell = (props: CourseAdminCardProps) => {
  const { variant = 'card', isDeleting, className, children } = props;
  const isBlock = variant === 'block';

  const stateClasses = twMerge(
    isBlock && 'bg-base-100 border-dashed border-secondary',
    isDeleting && 'opacity-50 pointer-events-none',
  );

  if (props.isDraggable) {
    return (
      <DragDropItem
        id={props.id}
        index={props.index}
        role={isBlock ? 'group' : undefined}
        disabled={props.disabled}
        dragIconHandle={props.dragIconHandle}
        tooltip={props.tooltip}
        hideDragIconHandleOnDisabled={props.hideDragIconHandleOnDisabled}
        className={{
          base: twMerge(
            shellBaseClasses,
            'gap-4',
            isBlock ? 'items-start' : 'items-center',
            stateClasses,
            className,
          ),
          div: 'flex flex-col gap-6 min-w-0 w-full',
          ...(isBlock && { button: 'top-1' }),
        }}
      >
        {children}
      </DragDropItem>
    );
  }

  const containerClasses = twMerge(
    shellBaseClasses,
    'flex flex-col gap-6',
    stateClasses,
    className,
  );

  if (isBlock) {
    return <Fieldset className={containerClasses}>{children}</Fieldset>;
  }
  return <div className={containerClasses}>{children}</div>;
};

const CourseAdminCardBase = (props: CourseAdminCardProps) => {
  const contextValue = useMemo<CourseAdminCardContextValue>(
    () => ({
      isDragging: !!props.isDragging,
      isDraggable: !!props.isDraggable,
    }),
    [props.isDragging, props.isDraggable],
  );

  return (
    <CourseAdminCardContext.Provider value={contextValue}>
      <CourseAdminCardShell {...props} />
    </CourseAdminCardContext.Provider>
  );
};

const Header = forwardRef<HTMLDivElement, CourseAdminCardHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge(
        'flex items-center justify-between gap-4 min-w-0',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);

const Leading = forwardRef<HTMLDivElement, CourseAdminCardLeadingProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge('flex items-center gap-4 min-w-0', className)}
      {...props}
    >
      {children}
    </div>
  ),
);

const Actions = forwardRef<HTMLDivElement, CourseAdminCardActionsProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge('flex items-center gap-4 shrink-0', className)}
      {...props}
    >
      {children}
    </div>
  ),
);

const Banner = forwardRef<HTMLImageElement, CourseAdminCardBannerProps>(
  ({ className, src, alt, ...props }, ref) => (
    <div className="h-10 aspect-video rounded-lg overflow-hidden shrink-0">
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={twMerge('w-full h-full object-cover', className)}
        {...props}
      />
    </div>
  ),
);

const LeadingIcon = ({
  children,
  className,
}: CourseAdminCardLeadingIconProps) => (
  <div className={twMerge('flex items-center shrink-0', className)}>
    {children}
  </div>
);

const Order = ({ value, className }: CourseAdminCardOrderProps) => {
  if (value === undefined) return null;
  const formatted = String(value).padStart(2, '0');
  return (
    <span
      className={twMerge(
        'text-base-content max-xl:text-sm text-base font-semibold shrink-0',
        className,
      )}
    >
      {formatted}
    </span>
  );
};

const TitleBlock = forwardRef<HTMLDivElement, CourseAdminCardTitleBlockProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge('flex flex-col min-w-0 gap-1', className)}
      {...props}
    >
      {children}
    </div>
  ),
);

const Title = forwardRef<HTMLSpanElement, CourseAdminCardTitleProps>(
  ({ className, children, ...props }, ref) => (
    <span
      ref={ref}
      className={twMerge(
        'text-base-content max-xl:text-sm text-base font-semibold truncate min-w-0',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  ),
);

const Description = forwardRef<
  HTMLSpanElement,
  CourseAdminCardDescriptionProps
>(({ className, children, ...props }, ref) => (
  <span
    ref={ref}
    className={twMerge('text-sm text-base-content truncate min-w-0', className)}
    {...props}
  >
    {children}
  </span>
));

const Badges = forwardRef<HTMLDivElement, CourseAdminCardBadgesProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge('flex items-center gap-4 flex-wrap', className)}
      {...props}
    >
      {children}
    </div>
  ),
);

const EditButton = ({
  onClick,
  loading,
  className,
}: CourseAdminCardEditButtonProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });
  const size = isXl ? 'sm' : 'xs';
  return (
    <Button
      variant="ghost"
      size={size}
      shape="square"
      onClick={onClick}
      aria-label={t('edit')}
      className={className}
    >
      <ConditionalRenderer
        condition={!loading}
        fallback={<Loading size={size} />}
      >
        <PencilSquareIcon className="max-xl:w-5 w-6 text-base-content" />
      </ConditionalRenderer>
    </Button>
  );
};

const DraggableInlineForm = ({
  id,
  index,
  isDraggable,
  children,
}: CourseAdminCardDraggableInlineFormProps) => (
  <ConditionalRenderer condition={!!isDraggable} fallback={children}>
    <DragDropItem
      id={id}
      index={index}
      disabled
      hideDragIconHandleOnDisabled
      className={{ base: 'mb-6' }}
    >
      {children}
    </DragDropItem>
  </ConditionalRenderer>
);

const Body = forwardRef<HTMLDivElement, CourseAdminCardBodyProps>(
  ({ className, children, ...props }, ref) => {
    const { isDragging } = useCourseAdminCardContext();
    return (
      <ConditionalRenderer condition={!isDragging}>
        <div ref={ref} className={twMerge('min-w-0', className)} {...props}>
          {children}
        </div>
      </ConditionalRenderer>
    );
  },
);

const NestedList = forwardRef<HTMLDivElement, CourseAdminCardNestedListProps>(
  ({ className, children, ...props }, ref) => {
    const { isDragging, isDraggable } = useCourseAdminCardContext();
    return (
      <ConditionalRenderer condition={!isDragging}>
        <div
          ref={ref}
          className={twMerge(
            'flex flex-col gap-6 min-w-0',
            isDraggable && 'w-[calc(100%+32px)] ml-[-32px]',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </ConditionalRenderer>
    );
  },
);

CourseAdminCardBase.displayName = 'CourseAdminCard';
Header.displayName = 'CourseAdminCard.Header';
Leading.displayName = 'CourseAdminCard.Leading';
Actions.displayName = 'CourseAdminCard.Actions';
Banner.displayName = 'CourseAdminCard.Banner';
LeadingIcon.displayName = 'CourseAdminCard.LeadingIcon';
Order.displayName = 'CourseAdminCard.Order';
TitleBlock.displayName = 'CourseAdminCard.TitleBlock';
Title.displayName = 'CourseAdminCard.Title';
Description.displayName = 'CourseAdminCard.Description';
Badges.displayName = 'CourseAdminCard.Badges';
EditButton.displayName = 'CourseAdminCard.EditButton';
DraggableInlineForm.displayName = 'CourseAdminCard.DraggableInlineForm';
Body.displayName = 'CourseAdminCard.Body';
NestedList.displayName = 'CourseAdminCard.NestedList';

export const CourseAdminCard = Object.assign(CourseAdminCardBase, {
  Header,
  Leading,
  Actions,
  Banner,
  LeadingIcon,
  Order,
  TitleBlock,
  Title,
  Description,
  Badges,
  EditButton,
  DraggableInlineForm,
  Body,
  NestedList,
  MoreActions,
});

export { MoreActions };
